import React from 'react';
import { Outlet, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCog } from '@fortawesome/free-solid-svg-icons';
import { BrandPresenceAreaRouteSyncContext, useBrandPresenceRouteSync } from './useGovernanceRouteSync';
import { BrandPresenceScreenQueryContext, IBrandPresenceScreenQuery, toBrandPresenceSearch } from './brandPresenceScreenQuery';
import {
  BRAND_PRESENCE_TAB_IN_STRIP_CLASSES,
  getBrandPresenceSubTabClassName,
  getBrandPresenceTabClassName,
} from './Components/BrandPresenceTabStyles';
import { BrandPresenceTabStrip } from './Components/BrandPresenceTabStrip';
import { loadBrandPresenceGlobalScripts } from './Components/loadBrandPresenceGlobalScript';
import { useBrandPresenceOnboarding } from './BrandPresenceOnboardingProvider';
import {
  BRAND_PRESENCE_PINNED_PART_PROPS,
  BrandPresencePinnedAreaContext,
  useElementHeight,
  useFocusClearOfPinnedParts,
  useHostViewport,
} from './useHostViewport';
import { AppParamsContext, IAppParams } from '../../AppParams';
import {
  BRAND_PRESENCE_AI_ANSWERS_PATH,
  BRAND_PRESENCE_AI_VISIBILITY_PATH,
  BRAND_PRESENCE_BASE_PATH,
  BRAND_PRESENCE_CONTENT_HEALTH_PATH,
  BRAND_PRESENCE_CONTENT_INVENTORY_PATH,
  BRAND_PRESENCE_CRAWLING_SETTINGS_PATH,
  BRAND_PRESENCE_ISSUES_PATH,
  BRAND_PRESENCE_JOBS_PATH,
  BRAND_PRESENCE_MONITORING_PATH,
  BRAND_PRESENCE_ONBOARDING_START_PATH,
  BRAND_PRESENCE_TAXONOMIES_PATH,
  brandPresenceAbsolutePath,
} from './brandPresencePaths';

type BrandPresencePrimaryTab = 'onboarding' | 'content-inventory' | 'issues' | 'analytics' | 'settings';

type BrandPresenceSettingsTab = 'taxonomies' | 'monitoring' | 'crawling' | 'onboarding' | 'jobs';

const ONBOARDING_PATH_PREFIX = brandPresenceAbsolutePath('onboarding');

/** A tab of the top row, in the strip that scrolls the row sideways where it is wider than the screen. */
const TOP_ROW_TAB_CLASSES = `${BRAND_PRESENCE_TAB_IN_STRIP_CLASSES} px-3 py-3.5`;

const PRIMARY_TAB_LABELS: Record<Exclude<BrandPresencePrimaryTab, 'settings'>, string> = {
  onboarding: 'Onboarding',
  'content-inventory': 'Content Inventory',
  issues: 'Issues',
  analytics: 'Analytics',
};

const PRIMARY_TAB_PATHS: Record<BrandPresencePrimaryTab, string> = {
  onboarding: BRAND_PRESENCE_ONBOARDING_START_PATH,
  'content-inventory': BRAND_PRESENCE_CONTENT_INVENTORY_PATH,
  issues: BRAND_PRESENCE_ISSUES_PATH,
  analytics: BRAND_PRESENCE_AI_VISIBILITY_PATH,
  settings: BRAND_PRESENCE_TAXONOMIES_PATH,
};

/** The screens of the Settings area, in the order its row shows them. Jobs keeps its own address. */
const SETTINGS_TABS: { tab: BrandPresenceSettingsTab; label: string; path: string }[] = [
  { tab: 'taxonomies', label: 'Taxonomies', path: BRAND_PRESENCE_TAXONOMIES_PATH },
  { tab: 'monitoring', label: 'Monitoring', path: BRAND_PRESENCE_MONITORING_PATH },
  { tab: 'crawling', label: 'Crawling', path: BRAND_PRESENCE_CRAWLING_SETTINGS_PATH },
  { tab: 'onboarding', label: 'Onboarding', path: BRAND_PRESENCE_ONBOARDING_START_PATH },
  { tab: 'jobs', label: 'Jobs', path: BRAND_PRESENCE_JOBS_PATH },
];

/**
 * Which primary tab the current path belongs to.
 *
 * The wizard highlights itself exactly while it has a primary tab of its own, and Settings whenever
 * it does not — which covers both a project that has been through it and an area that has no answer
 * yet, since in both the Settings row is where the wizard is offered. Any other unmatched path, such
 * as the area's bare path while the destination is being resolved, highlights nothing.
 */
export function getActiveTab(pathname: string, hasOnboardingPrimaryTab: boolean): BrandPresencePrimaryTab {
  if (
    pathname.startsWith(BRAND_PRESENCE_TAXONOMIES_PATH) ||
    pathname.startsWith(BRAND_PRESENCE_MONITORING_PATH) ||
    pathname.startsWith(BRAND_PRESENCE_CRAWLING_SETTINGS_PATH)
  ) {
    return 'settings';
  }

  // Jobs belongs to the Settings area; its address stays where links and bookmarks expect it.
  if (pathname.startsWith(brandPresenceAbsolutePath('jobs'))) {
    return 'settings';
  }

  if (pathname.startsWith(brandPresenceAbsolutePath('analytics'))) {
    return 'analytics';
  }

  if (pathname.startsWith(BRAND_PRESENCE_ISSUES_PATH)) {
    return 'issues';
  }

  if (pathname.startsWith(BRAND_PRESENCE_CONTENT_INVENTORY_PATH)) {
    return 'content-inventory';
  }

  if (pathname.startsWith(brandPresenceAbsolutePath('optimize'))) {
    return 'content-inventory';
  }

  if (pathname.startsWith(ONBOARDING_PATH_PREFIX)) {
    return hasOnboardingPrimaryTab ? 'onboarding' : 'settings';
  }

  return 'onboarding';
}

export function BrandPresenceNavigation() {
  const location = useLocation();
  const { status, isOnboardingCompleted } = useBrandPresenceOnboarding();

  React.useEffect(() => {
    void loadBrandPresenceGlobalScripts().catch((error) => {
      console.error('Failed to load Brand Presence global scripts', error);
    });
  }, []);

  // The wizard keeps a primary tab of its own only while the area knows the project has not been
  // through it. An unresolved or a failed read never puts one there, because a tab that appears and
  // then disappears is worse than one the Settings row carries in every state.
  const hasOnboardingPrimaryTab = status === 'resolved' && !isOnboardingCompleted;

  const activeTab = getActiveTab(location.pathname, hasOnboardingPrimaryTab);
  const activeAnalyticsSubTab = location.pathname.startsWith(BRAND_PRESENCE_AI_VISIBILITY_PATH)
    ? 'ai-visibility'
    : location.pathname.startsWith(BRAND_PRESENCE_AI_ANSWERS_PATH)
      ? 'ai-answers'
      : 'content-health';
  const activeSettingsSubTab: BrandPresenceSettingsTab = location.pathname.startsWith(ONBOARDING_PATH_PREFIX)
    ? 'onboarding'
    : location.pathname.startsWith(brandPresenceAbsolutePath('jobs'))
      ? 'jobs'
      : location.pathname.startsWith(BRAND_PRESENCE_MONITORING_PATH)
        ? 'monitoring'
        : location.pathname.startsWith(BRAND_PRESENCE_CRAWLING_SETTINGS_PATH)
          ? 'crawling'
          : 'taxonomies';

  useBrandPresenceRouteSync({
    embedPrefix: BRAND_PRESENCE_BASE_PATH,
    appPrefix: BRAND_PRESENCE_BASE_PATH,
    // The area's own bare path, never the wizard: where a handover that does not name a screen ends
    // up is the index route's decision, and it is the only place that has the flags to make it.
    defaultEmbedPath: BRAND_PRESENCE_BASE_PATH,
    skipBasePathUpdate: true,
  });

  const primaryTabs: Exclude<BrandPresencePrimaryTab, 'settings'>[] = [
    ...(hasOnboardingPrimaryTab ? (['onboarding'] as const) : []),
    'content-inventory',
    'issues',
    'analytics',
  ];

  const getTabClassName = (tab: BrandPresencePrimaryTab) =>
    getBrandPresenceTabClassName(activeTab === tab, TOP_ROW_TAB_CLASSES, 'under-label');

  // Which tab is in view follows the tab model rather than the router's own path matching — the cog
  // stands for a group of screens, and the wizard sits under it once it has been finished — so these
  // are plain links carrying the state this component derived. The treatments are a colour and an
  // underline, which say nothing to a reader that cannot see them, so the tab also states it.
  const getTabAriaCurrent = (isActive: boolean) => (isActive ? ('page' as const) : undefined);

  const settingsActive = activeTab === 'settings';

  const hostScrollsFrame = !!(React.useContext(AppParamsContext) as IAppParams | null)?.hostScrollsFrame;
  const hostViewport = useHostViewport();
  const hostTop = hostScrollsFrame ? hostViewport.top : 0;
  const hostHeight = hostScrollsFrame ? hostViewport.height : 0;
  const navigationRef = React.useRef<HTMLDivElement>(null);
  const navigationHeight = useElementHeight(navigationRef);
  const [pinnedHeaderHeight, setPinnedHeaderHeight] = React.useState(0);
  const pinnedHeight = navigationHeight + pinnedHeaderHeight;
  const pinnedArea = React.useMemo(
    () => ({ hostScrollsFrame, hostTop, hostHeight, navigationHeight, pinnedHeight, onPinnedHeaderHeight: setPinnedHeaderHeight }),
    [hostScrollsFrame, hostTop, hostHeight, navigationHeight, pinnedHeight]
  );

  // The focus scrolling of whatever scrolls the area knows nothing of the parts pinned over it. A body
  // that scrolls a fixed-height frame keeps them out of its own through its scroll padding; a host that
  // scrolls the frame is asked to bring a focused element clear of them.
  const areaRef = React.useRef<HTMLDivElement>(null);
  useFocusClearOfPinnedParts({ areaRef, hostScrollsFrame, hostTop, pinnedHeight });
  React.useEffect(() => {
    if (hostScrollsFrame) {
      return;
    }
    document.body.style.scrollPaddingTop = `${pinnedHeight}px`;
    return () => {
      document.body.style.scrollPaddingTop = '';
    };
  }, [hostScrollsFrame, pinnedHeight]);

  // Back from Settings returns to the last page of the area the reader was on, with its filters; a
  // Settings screen opened directly goes back to the content inventory. A screen that keeps its
  // filters in the host page's address reports them, since its own address does not carry them.
  const lastAreaLocationRef = React.useRef<{ pathname: string; search: string } | undefined>(undefined);
  const screenQueryRef = React.useRef<IBrandPresenceScreenQuery | undefined>(undefined);
  React.useEffect(() => {
    if (!settingsActive && location.pathname !== BRAND_PRESENCE_BASE_PATH) {
      lastAreaLocationRef.current = { pathname: location.pathname, search: location.search };
    }
  }, [location.pathname, location.search, settingsActive]);
  const lastAreaLocation = lastAreaLocationRef.current;
  const reportedScreenQuery =
    lastAreaLocation && screenQueryRef.current?.pathname === lastAreaLocation.pathname ? screenQueryRef.current.query : undefined;
  // A plain link: when it moves the frame, the route sync reports the whole address — query
  // included — to the host page. A modified click that opens a new view instead moves nothing here,
  // so nothing is reported and the host page's history gains no entry.
  const backPath = lastAreaLocation
    ? `${lastAreaLocation.pathname}${reportedScreenQuery ? toBrandPresenceSearch(reportedScreenQuery) : lastAreaLocation.search}`
    : BRAND_PRESENCE_CONTENT_INVENTORY_PATH;

  // The area takes its content's height, so there is no pane of its own that scrolls: in a frame of a
  // fixed height the body scrolls and the navigation sticks to its top; in a frame the host page
  // sizes to its content, the navigation is moved down to the line the host reports.
  return (
    <div ref={areaRef} className="w-full d-flex flex-column" id="brand-presence-page-container">
      <div
        ref={navigationRef}
        {...BRAND_PRESENCE_PINNED_PART_PROPS}
        className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/85 backdrop-blur-md"
        style={hostTop ? { transform: `translateY(${hostTop}px)` } : undefined}
      >
        <nav className="flex w-full items-center gap-1 px-4" aria-label={settingsActive ? 'Settings' : 'Brand Presence'}>
          {settingsActive ? (
            <>
              <Link
                to={backPath}
                className="me-2 inline-flex h-8 shrink-0 items-center gap-2 rounded-lg px-2.5 text-sm font-semibold text-slate-600 no-underline transition-colors duration-150 hover:bg-slate-100 hover:text-slate-900 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="text-xs" aria-hidden="true" />
                Back
              </Link>
              <span aria-hidden="true" className="me-1 h-5 w-px shrink-0 bg-slate-200" />
            </>
          ) : null}
          {/* Back and the cog stay in view; on a screen narrower than the row, the tabs between
              them scroll sideways in this strip, which shows the one the reader is on. */}
          <BrandPresenceTabStrip className="flex-1" inView={settingsActive ? activeSettingsSubTab : activeTab}>
            {settingsActive
              ? SETTINGS_TABS.map(({ tab, label, path: tabPath }) => (
                  <Link
                    key={tab}
                    to={tabPath}
                    className={getBrandPresenceTabClassName(activeSettingsSubTab === tab, TOP_ROW_TAB_CLASSES, 'under-label')}
                    aria-current={getTabAriaCurrent(activeSettingsSubTab === tab)}
                  >
                    {label}
                  </Link>
                ))
              : primaryTabs.map((tab) => (
                  <Link
                    key={tab}
                    to={PRIMARY_TAB_PATHS[tab]}
                    className={getTabClassName(tab)}
                    aria-current={getTabAriaCurrent(activeTab === tab)}
                  >
                    {PRIMARY_TAB_LABELS[tab]}
                  </Link>
                ))}
          </BrandPresenceTabStrip>
          <Link
            to={PRIMARY_TAB_PATHS.settings}
            className={`ms-auto inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg no-underline transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
              settingsActive ? 'bg-primary/10 text-primary' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
            }`}
            aria-current={getTabAriaCurrent(settingsActive)}
            aria-label="Settings"
            title="Settings"
          >
            <FontAwesomeIcon icon={faCog} className={`transition-transform duration-500 ${settingsActive ? 'rotate-90' : ''}`} />
          </Link>
        </nav>
        {activeTab === 'analytics' ? (
          <div className="px-4 pt-2 pb-3 motion-safe:animate-bp-slide-down">
            <nav className="inline-flex items-center gap-0.5 rounded-xl bg-slate-100/90 p-1" aria-label="Analytics">
              <Link
                to={BRAND_PRESENCE_AI_VISIBILITY_PATH}
                className={getBrandPresenceSubTabClassName(activeAnalyticsSubTab === 'ai-visibility')}
                aria-current={getTabAriaCurrent(activeAnalyticsSubTab === 'ai-visibility')}
              >
                AI Visibility
              </Link>
              <Link
                to={BRAND_PRESENCE_CONTENT_HEALTH_PATH}
                className={getBrandPresenceSubTabClassName(activeAnalyticsSubTab === 'content-health')}
                aria-current={getTabAriaCurrent(activeAnalyticsSubTab === 'content-health')}
              >
                Content Health
              </Link>
              <Link
                to={BRAND_PRESENCE_AI_ANSWERS_PATH}
                className={getBrandPresenceSubTabClassName(activeAnalyticsSubTab === 'ai-answers')}
                aria-current={getTabAriaCurrent(activeAnalyticsSubTab === 'ai-answers')}
              >
                AI answers
              </Link>
            </nav>
          </div>
        ) : null}
      </div>
      <div className="brand-presence-screen flex-grow-1 bg-slate-50">
        <BrandPresencePinnedAreaContext.Provider value={pinnedArea}>
          <BrandPresenceScreenQueryContext.Provider value={screenQueryRef}>
            <BrandPresenceAreaRouteSyncContext.Provider value={true}>
              <Outlet />
            </BrandPresenceAreaRouteSyncContext.Provider>
          </BrandPresenceScreenQueryContext.Provider>
        </BrandPresencePinnedAreaContext.Provider>
      </div>
    </div>
  );
}
